import * as Popover from "@radix-ui/react-popover";
import type { KeyboardEvent, ReactNode } from "react";
import React, { useMemo, useRef, useState } from "react";

import { type T_KitCssVars } from "./InteractiveSurface";
import { TextField } from "./TextField";
import "./kit.css";

export type T_DropdownMenuItem = {
  description?: string;
  disabled?: boolean;
  id: string;
  label: string;
  selected?: boolean;
};

export type T_DropdownMenuInteraction = {
  searchUsed: boolean;
};

export type T_DropdownMenuProps = {
  align?: "left" | "right";
  defaultOpen?: boolean;
  items: readonly T_DropdownMenuItem[];
  menuAccessibilityLabel: string;
  menuWidth?: number;
  onDismiss?: (interaction: T_DropdownMenuInteraction) => void;
  onOpen?: () => void;
  onSelect: (
    item: T_DropdownMenuItem,
    interaction: T_DropdownMenuInteraction,
  ) => void;
  /**
   * Trigger CONTENT only — rendered inside the Popover trigger `<button>`.
   * Must not contain interactive elements (buttons/links): nesting them
   * produces invalid button-in-button HTML. Pass spans/icons/text.
   */
  renderTrigger: (state: { isOpen: boolean }) => ReactNode;
  searchable?: boolean;
  searchPlaceholder?: string;
  triggerAccessibilityLabel: string;
};

/**
 * Canonical anchored menu — web mirror of the mobile DropdownMenu. Radix
 * Popover supplies anchoring/collision positioning, outside-click and Escape
 * dismissal, and focus restore (recorded divergence: mobile positions with
 * the pure dropdown-menu-layout module; web delegates to Radix). Arrow-key
 * navigation is a roving focus across items; search uses the kit TextField.
 */
export function DropdownMenu({
  align = "right",
  defaultOpen = false,
  items,
  menuAccessibilityLabel,
  menuWidth,
  onDismiss,
  onOpen,
  onSelect,
  renderTrigger,
  searchable = false,
  searchPlaceholder = "Search",
  triggerAccessibilityLabel,
}: T_DropdownMenuProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [query, setQuery] = useState("");
  const listRef = useRef<HTMLDivElement | null>(null);

  const normalizedQuery = query.trim().toLowerCase();
  const interaction: T_DropdownMenuInteraction = {
    searchUsed: normalizedQuery.length > 0,
  };
  const visibleItems = useMemo(() => {
    if (!normalizedQuery) return items;
    return items.filter((item) =>
      `${item.label} ${item.description ?? ""}`
        .toLowerCase()
        .includes(normalizedQuery),
    );
  }, [items, normalizedQuery]);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      onOpen?.();
    } else {
      onDismiss?.(interaction);
      setQuery("");
    }
  };

  const focusItemAt = (index: number) => {
    const buttons = listRef.current?.querySelectorAll<HTMLButtonElement>(
      "button.lk-dropdown__item:not(:disabled)",
    );
    if (!buttons || buttons.length === 0) return;
    const clamped =
      ((index % buttons.length) + buttons.length) % buttons.length;
    buttons[clamped]?.focus();
  };

  const handleListKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const buttons = Array.from(
      listRef.current?.querySelectorAll<HTMLButtonElement>(
        "button.lk-dropdown__item:not(:disabled)",
      ) ?? [],
    );
    const activeElement =
      document.activeElement instanceof HTMLButtonElement
        ? document.activeElement
        : null;
    const currentIndex = activeElement ? buttons.indexOf(activeElement) : -1;

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        focusItemAt(currentIndex + 1);
        break;
      case "ArrowUp":
        event.preventDefault();
        // From nothing focused, ArrowUp lands on the LAST item (roving
        // convention); -1 - 1 would wrap to second-to-last.
        focusItemAt(
          currentIndex === -1 ? buttons.length - 1 : currentIndex - 1,
        );
        break;
      case "Home":
        // Leave caret navigation alone while typing in the search field.
        if (event.target instanceof HTMLInputElement) break;
        event.preventDefault();
        focusItemAt(0);
        break;
      case "End":
        if (event.target instanceof HTMLInputElement) break;
        event.preventDefault();
        focusItemAt(buttons.length - 1);
        break;
      default:
        break;
    }
  };

  const triggerVars: T_KitCssVars = {
    "--lk-bg": "transparent",
    "--lk-bg-pressed": "var(--lattice-dark-grey-dark-6)",
  };

  return (
    <Popover.Root onOpenChange={handleOpenChange} open={isOpen}>
      <Popover.Trigger
        aria-label={triggerAccessibilityLabel}
        className="lk-interactive lk-dropdown__trigger"
        style={triggerVars}
      >
        {renderTrigger({ isOpen })}
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align={align === "right" ? "end" : "start"}
          className="lk-dropdown"
          onKeyDown={handleListKeyDown}
          sideOffset={6}
          style={menuWidth !== undefined ? { width: menuWidth } : undefined}
        >
          {searchable ? (
            <div className="lk-dropdown__search">
              <TextField
                autoFocus
                onChange={(event) => setQuery(event.target.value)}
                placeholder={searchPlaceholder}
                type="search"
                value={query}
              />
            </div>
          ) : null}
          <div
            aria-label={menuAccessibilityLabel}
            className="lk-dropdown__list"
            ref={listRef}
            role="menu"
          >
            {visibleItems.length === 0 ? (
              <div className="lk-dropdown__empty">No matches found</div>
            ) : (
              visibleItems.map((item) => (
                <button
                  aria-disabled={item.disabled || undefined}
                  className={[
                    "lk-dropdown__item",
                    item.selected ? "lk-dropdown__item--selected" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  disabled={item.disabled}
                  key={item.id}
                  onClick={() => {
                    onSelect(item, interaction);
                    setIsOpen(false);
                    setQuery("");
                  }}
                  role="menuitem"
                  type="button"
                >
                  <span className="lk-dropdown__item-copy">
                    <span className="lk-dropdown__item-label">
                      {item.label}
                    </span>
                    {item.description !== undefined &&
                    item.description !== "" ? (
                      <span className="lk-dropdown__item-description">
                        {item.description}
                      </span>
                    ) : null}
                  </span>
                  {item.selected ? (
                    <svg
                      aria-hidden
                      className="lk-dropdown__check"
                      fill="none"
                      height={17}
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.5}
                      viewBox="0 0 24 24"
                      width={17}
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  ) : null}
                </button>
              ))
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
